import { Button, Card, Sheet, Stack, Table, Typography } from '@healthinal/ui';
import { useEffect, useRef, useState } from 'react';

type MasterdataTableProps = {
  title: string;
  headerActions?: React.ReactNode;
  isLoading?: boolean;
  isEmpty?: boolean;
  emptyText?: string;
  colSpan: number;
  page: number;
  totalPages: number;
  onPrevPage: () => void;
  onNextPage: () => void;
  disablePrev?: boolean;
  disableNext?: boolean;
  header: React.ReactNode;
  children: React.ReactNode;
};

export function MasterdataTable({
  title,
  headerActions,
  isLoading = false,
  isEmpty = false,
  emptyText = 'Keine Daten vorhanden.',
  colSpan,
  page,
  totalPages,
  onPrevPage,
  onNextPage,
  disablePrev,
  disableNext,
  header,
  children,
}: MasterdataTableProps) {
  const visiblePage = totalPages === 0 ? 0 : page + 1;
  const [isPaginationPending, setIsPaginationPending] = useState(false);
  const isPaginationPendingRef = useRef(false);
  const isPrevDisabled = disablePrev || isPaginationPending;
  const isNextDisabled = disableNext || isPaginationPending;

  useEffect(() => {
    if (!isLoading) {
      isPaginationPendingRef.current = false;
      setIsPaginationPending(false);
    }
  }, [isLoading, page]);

  const handlePrevPage = () => {
    if (isPrevDisabled || isPaginationPendingRef.current) return;

    isPaginationPendingRef.current = true;
    setIsPaginationPending(true);
    onPrevPage();
  };

  const handleNextPage = () => {
    if (isNextDisabled || isPaginationPendingRef.current) return;

    isPaginationPendingRef.current = true;
    setIsPaginationPending(true);
    onNextPage();
  };

  return (
    <Card style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ width: '100%', minWidth: 0, gap: 1, flexWrap: 'wrap' }}>
        <Typography level="h3">{title}</Typography>

        <Stack direction="row" alignItems="center" sx={{ gap: 1, minWidth: 0, ml: 'auto', flexWrap: 'wrap' }}>
          {headerActions}
        </Stack>
      </Stack>

      <Sheet sx={{ maxHeight: '60dvh', overflow: 'auto', width: '100%', minWidth: 0 }}>
        <Table
          stickyHeader
          hoverRow
          sx={{
            width: '100%',
            tableLayout: 'fixed',
            '& th, & td': {
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              paddingLeft: '8px',
              paddingRight: '8px',
              paddingTop: '6px',
              paddingBottom: '6px',
            },
          }}>
          <thead>
            <tr>{header}</tr>
          </thead>

          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={colSpan}>
                  <Typography>Lade Daten...</Typography>
                </td>
              </tr>
            ) : isEmpty ? (
              <tr>
                <td colSpan={colSpan}>
                  <Typography>{emptyText}</Typography>
                </td>
              </tr>
            ) : (
              children
            )}
          </tbody>
        </Table>
      </Sheet>

      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 2 }}>
        <Typography level="body-sm">
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
            }}>
            <span>Seite</span>
            <span translate="no" className="notranslate">
              {visiblePage}
            </span>
            <span>von</span>
            <span translate="no" className="notranslate">
              {totalPages}
            </span>
          </span>
        </Typography>

        <Stack direction="row" spacing={1}>
          <Button variant="outlined" disabled={isPrevDisabled} onClick={handlePrevPage}>
            Zurück
          </Button>

          <Button variant="outlined" disabled={isNextDisabled} onClick={handleNextPage}>
            Weiter
          </Button>
        </Stack>
      </Stack>
    </Card>
  );
}
